// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 62.47.0 BC-6 (owner answer O-2, 2026-11-04; third `legal` reading
 * P2-C3) — a recorded identifier value is rendered inert: plain text, never the
 * target of a link, on every surface that renders a Forseti answer.
 *
 * ## Why a link on a recorded identifier matters
 *
 * A resolver-style identifier is itself a URL, and other recorded identifier
 * values (an item number, a lot, a serial) are exactly what a resolver URL is
 * built from. A link whose target carries one is a request that hands record data
 * to whoever answers that URL. ComplianceOne records identifiers; it does not
 * send them anywhere, so no answer it renders may turn one into a link.
 *
 * ## The rule, and why it is an href check
 *
 * The model can quote a value as a bare URL, as `<url>`, as `[label](url)`, or
 * build a new URL around a value it read — and it can re-type the value in any of
 * those forms. Marking values in the tool output would rely on the model copying
 * the mark. So the rule is applied where a link is CREATED: every renderer asks
 * {@link hrefCarriesInertValue} before it makes a link, and a target that
 * contains any recorded identifier value of the conversation (compared without
 * case, raw and percent-decoded) is rendered as its text instead. Every other
 * link is untouched.
 *
 * ## What counts as a recorded identifier value
 *
 * {@link collectRecordedIdentifierValues} reads a tool result by SHAPE, not by
 * tool or field name: any object carrying a string `scheme_code` and a string
 * `value` is a recorded trace identifier (`TraceIdentifier`), wherever it sits —
 * a product's or batch's `identifiers[]`, a trace event's `observed_identifier`,
 * or a record another tool returns tomorrow. No scheme, instrument or format is
 * named (R-46): what makes a value inert is that the platform recorded it as an
 * identifier, not what it looks like.
 *
 * Conservative by construction: a short value that also occurs inside an
 * ordinary URL makes that URL plain text too. An over-match costs a click; an
 * under-match would be a link carrying record data.
 */
/**
 * The most recorded identifier values one chat message keeps. Past it the
 * message keeps none and is marked `inert_all_links`: every link in it renders
 * as text, so the rule never runs against a partial list.
 */
export const INERT_VALUES_MAX_PER_MESSAGE = 5_000;
/** The deepest a tool result is walked, and the most nodes read: a bound, never a limit a real result reaches. */
const MAX_DEPTH = 24;
const MAX_NODES = 200_000;
/**
 * Every recorded identifier value in `input` (a parsed tool result), added to
 * `into`. Reads objects with a string `scheme_code` and a non-empty string
 * `value`; walks arrays and plain objects; ignores everything else.
 */
export function collectRecordedIdentifierValues(input, into = new Set()) {
    let nodes = 0;
    const walk = (node, depth) => {
        if (node === null || typeof node !== 'object' || depth > MAX_DEPTH)
            return;
        if (++nodes > MAX_NODES)
            return;
        if (Array.isArray(node)) {
            for (const item of node)
                walk(item, depth + 1);
            return;
        }
        const record = node;
        if (typeof record.scheme_code === 'string' &&
            typeof record.value === 'string' &&
            record.value !== '') {
            into.add(record.value);
        }
        for (const key of Object.keys(record))
            walk(record[key], depth + 1);
    };
    walk(input, 0);
    return into;
}
/** `decodeURIComponent`, or the input when it is not a valid escape sequence. */
function safeDecode(text) {
    try {
        return decodeURIComponent(text);
    }
    catch {
        return text;
    }
}
/**
 * Does `href` carry any of `values`? Compared without case, against the target
 * as written and percent-decoded (once and twice, so `%252F` is read as `/`), and
 * each value also percent-encoded. An empty value matches nothing.
 */
export function hrefCarriesInertValue(href, values) {
    if (!href || !values)
        return false;
    const raw = href.toLowerCase();
    const once = safeDecode(href);
    const forms = [raw, once.toLowerCase(), safeDecode(once).toLowerCase()];
    for (const value of values) {
        if (typeof value !== 'string' || value === '')
            continue;
        const needle = value.toLowerCase();
        const encoded = encodeURIComponent(value).toLowerCase();
        if (forms.some((form) => form.includes(needle) || form.includes(encoded)))
            return true;
    }
    return false;
}
/** May a renderer make a link to `href` under `rule`? */
export function linkTargetIsInert(href, rule) {
    if (!rule)
        return false;
    if (rule.all)
        return true;
    return hrefCarriesInertValue(href, rule.values);
}
//# sourceMappingURL=inert-record-values.js.map